import React, { useState, useMemo } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import { AccessTime, Search, DateRange, Clear } from "@mui/icons-material";
import { useQuery } from "react-query";
import { useAuth0 } from "@auth0/auth0-react";
import drawdownService from "../../../../../services/drawdownService";
import { CACHE_TIMES } from "../constants/drawdownConstants";

const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) {
    return { date: "Invalid date", time: "Invalid time" };
  }

  return {
    date: date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
    time: date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
};

const PreviousRunsList = ({ onSelectRun }) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortAscending, setSortAscending] = useState(false); // false = newest first

  const {
    data: runs = [],
    isLoading: loading,
    error,
  } = useQuery(
    "drawdown-runs",
    () => drawdownService.getRuns(getAccessTokenSilently, isAuthenticated),
    {
      enabled: isAuthenticated,
      staleTime: CACHE_TIMES.RUNS_LIST,
      refetchOnWindowFocus: false,
    }
  );

  // Filter and sort runs
  const filteredAndSortedRuns = useMemo(() => {
    let filtered = runs;

    // Filter by search term
    if (searchTerm.trim()) {
      filtered = runs.filter((run) =>
        run.proposed_well_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort by date
    filtered.sort((a, b) => {
      const dateA = new Date(a.run_timestamp);
      const dateB = new Date(b.run_timestamp);
      return sortAscending ? dateA - dateB : dateB - dateA;
    });

    return filtered;
  }, [runs, searchTerm, sortAscending]);

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={2}
        py={4}
      >
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary">
          Loading previous runs...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        <Typography variant="body2">
          {error?.message || "Failed to load previous runs"}
        </Typography>
      </Alert>
    );
  }

  if (runs.length === 0) {
    return (
      <Alert severity="info">
        <Typography variant="body2">
          No previous runs found. Create the first analysis to get started.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Typography variant="h6" sx={{ fontSize: "0.9rem", flexGrow: 1 }}>
          Previous Analysis Runs ({filteredAndSortedRuns.length})
        </Typography>
        <Tooltip
          title={sortAscending ? "Sort: Oldest first" : "Sort: Newest first"}
        >
          <IconButton
            onClick={() => setSortAscending(!sortAscending)}
            size="small"
            sx={{
              color: "primary.main",
              transform: sortAscending ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease-in-out",
            }}
          >
            <DateRange />
          </IconButton>
        </Tooltip>
      </Box>

      <TextField
        size="small"
        placeholder="Search runs..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <Search sx={{ color: "action.disabled", mr: 1, fontSize: 18 }} />
          ),
          endAdornment: searchTerm && (
            <IconButton
              onClick={() => setSearchTerm("")}
              size="small"
              sx={{ p: 0.5 }}
            >
              <Clear sx={{ fontSize: 16 }} />
            </IconButton>
          ),
        }}
        sx={{
          width: "100%",
          mb: 2,
          "& .MuiInputBase-root": {
            fontSize: "0.875rem",
            minHeight: "32px",
          },
          "& .MuiInputBase-input": {
            py: 0.5,
          },
        }}
      />

      {filteredAndSortedRuns.length === 0 ? (
        <Alert severity="info">
          <Typography variant="body2">
            No runs found matching "{searchTerm}". Try a different search term.
          </Typography>
        </Alert>
      ) : (
        <List sx={{ width: "100%", p: 0 }}>
          {filteredAndSortedRuns.map(
            ({ run_ndx, proposed_well_name, run_timestamp }) => {
              const { date, time } = formatTimestamp(run_timestamp);

              return (
                <ListItem
                  key={run_ndx}
                  disablePadding
                  sx={{
                    mb: 1,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    "&:hover": {
                      borderColor: "primary.main",
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <ListItemButton
                    onClick={() => onSelectRun(run_ndx)}
                    sx={{
                      flexDirection: "column",
                      alignItems: "flex-start",
                      py: 1.5,
                      px: 2,
                    }}
                  >
                    <Box sx={{ width: "100%", mb: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          mb: 0.5,
                          lineHeight: 1.2,
                        }}
                      >
                        {proposed_well_name}
                      </Typography>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <AccessTime
                          sx={{ fontSize: 14, color: "text.secondary" }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {date} at {time}
                        </Typography>
                      </Stack>
                    </Box>

                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Chip
                        label={`Run #${run_ndx}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: "0.7rem" }}
                      />

                      <Chip
                        label="View Results"
                        size="small"
                        color="primary"
                        variant="filled"
                        sx={{
                          fontSize: "0.7rem",
                          display: { xs: "none", md: "flex" },
                        }}
                      />
                    </Box>
                  </ListItemButton>
                </ListItem>
              );
            }
          )}
        </List>
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1, display: "block" }}
      >
        Click on any run to view detailed results and analysis.
      </Typography>
    </Box>
  );
};

export default PreviousRunsList;
